import { useEffect } from "react";
import { useLocation, Outlet } from "react-router-dom";

/**
 * Router-level scroll behaviour.
 *
 * React Router keeps the window scroll position across navigations and ignores
 * `#hash` targets entirely. Without this, following a CTA from halfway down the
 * landing page lands you halfway down the next page, and an in-page link like
 * `/#earnings` does nothing at all.
 *
 * Rendered once as a pathless layout route so it covers every page.
 */
function ScrollManager() {
  // Deliberately keyed on pathname and hash only. Pages that mirror state into
  // the query string — the revenue calculator writes its sliders there on every
  // change — must not be yanked back to the top mid-interaction.
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Wait a frame so the destination page has painted and the anchor exists.
    const frame = window.requestAnimationFrame(() => {
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;

      if (hash) {
        const target = document.getElementById(hash.slice(1));
        if (target) {
          target.scrollIntoView({
            behavior: prefersReducedMotion ? "auto" : "smooth",
            block: "start",
          });
          return;
        }
      }

      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [pathname, hash]);

  return <Outlet />;
}

export default ScrollManager;
